import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import {
  BookOpen, CheckCircle2, AlertCircle, Loader2, RefreshCw, Trash2, KeyRound,
  Download, Copy, Upload, ChevronDown, ChevronUp, FileText, Clock, ShieldCheck,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Tally connect panel — pair-token flow + manual XML upload fallback.
 * Mirrors the architecture of PetpoojaConnectPanel but uses a one-time pairing
 * token instead of long-lived API keys (because Tally lives on the accountant's PC).
 */
export const TallyConnectPanel = ({ client, isAdmin }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState({ load: false, pair: false, disc: false, upload: false });
  const [pairing, setPairing] = useState(null);     // {pairing_token, instructions}
  const [copied, setCopied] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsOpen, setLogsOpen] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState(null);

  const auth = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('access_token') || ''}` },
  });

  const loadAll = useCallback(async () => {
    if (!client?.id) return;
    setBusy(b => ({ ...b, load: true })); setError(null);
    try {
      const [st, lg] = await Promise.all([
        axios.get(`${API}/clients/${client.id}/integrations/tally`, auth()),
        axios.get(`${API}/clients/${client.id}/integrations/tally/logs?limit=10`, auth()),
      ]);
      setStatus(st.data);
      setLogs(lg.data?.logs || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(b => ({ ...b, load: false }));
    }
  }, [client?.id]);

  useEffect(() => {
    setPairing(null); setUploadResult(null); setError(null);
    loadAll();
  }, [loadAll]);

  const generatePairing = async () => {
    setBusy(b => ({ ...b, pair: true })); setError(null);
    try {
      const { data } = await axios.post(
        `${API}/clients/${client.id}/integrations/tally/pair`, {}, auth(),
      );
      setPairing(data);
      await loadAll();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(b => ({ ...b, pair: false }));
    }
  };

  const copyToken = () => {
    if (!pairing?.pairing_token) return;
    navigator.clipboard.writeText(pairing.pairing_token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Tally for this client? Pairing will be revoked — the connector on the accountant\'s PC will stop syncing. (Tally vouchers already synced are kept.)')) return;
    setBusy(b => ({ ...b, disc: true })); setError(null);
    try {
      await axios.delete(`${API}/clients/${client.id}/integrations/tally`, auth());
      setPairing(null);
      await loadAll();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(b => ({ ...b, disc: false }));
    }
  };

  const uploadXml = async (file) => {
    if (!file) return;
    setBusy(b => ({ ...b, upload: true })); setError(null); setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(
        `${API}/clients/${client.id}/integrations/tally/upload-xml`, fd,
        { headers: { ...auth().headers, 'Content-Type': 'multipart/form-data' } },
      );
      setUploadResult(data);
      await loadAll();
    } catch (e) {
      setUploadResult({ ok: false, error: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(b => ({ ...b, upload: false }));
    }
  };

  if (busy.load) {
    return (
      <div className="flex items-center gap-2 text-sm text-text-muted py-6 justify-center" data-testid="tally-panel-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading Tally integration state…
      </div>
    );
  }
  if (!status) return null;

  const isActive = status.connected;
  const isAwaiting = status.status === 'awaiting_connector';
  const isManualOnly = status.status === 'manual_upload_only';

  return (
    <div className="space-y-4" data-testid="tally-connect-panel">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-background-secondary rounded-md px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-blue-100 text-blue-700">
            <BookOpen className="h-5 w-5" />
          </div>
          <div>
            <div className="text-sm font-semibold text-text-primary">Tally Prime connection</div>
            <div className="text-xs text-text-secondary">
              Connector Agent (free utility on the accountant's PC) pushes vouchers nightly · Encrypted in transit
            </div>
          </div>
        </div>
        <StatusPill status={status.status} isActive={isActive} isAwaiting={isAwaiting} isManualOnly={isManualOnly} />
      </div>

      {/* Status detail */}
      {(isActive || isManualOnly) && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
          <InfoTile label="Vouchers synced" value={status.voucher_count?.toLocaleString() || '0'} />
          <InfoTile label="Last sync"
                    value={status.last_sync_at ? new Date(status.last_sync_at).toLocaleString() : 'Never'}
                    badge={status.last_sync_status} />
          <InfoTile label="Connector"
                    value={status.connector_host ? `${status.connector_host} · v${status.connector_version}` : 'Manual upload only'}
                    mono />
        </div>
      )}

      {/* PAIRING — first time setup */}
      {!isActive && !isManualOnly && isAdmin && !pairing && (
        <div className="bg-white border-2 border-dashed border-blue-300 rounded-md p-5 space-y-3" data-testid="tally-pair-prompt">
          <div className="flex items-start gap-3">
            <KeyRound className="h-6 w-6 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-text-primary mb-1">Step 1 · Generate a pairing token</h3>
              <p className="text-xs text-text-secondary">
                One-time token. You'll share this + the download link with the accountant.
                They paste it into the Tally Connector installer ONCE — afterwards it's no longer needed.
              </p>
            </div>
          </div>
          <Button onClick={generatePairing} disabled={busy.pair}
                  className="bg-primary hover:bg-primary-hover"
                  data-testid="tally-generate-token-btn">
            {busy.pair ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <KeyRound className="h-4 w-4 mr-2" />}
            Generate pairing token
          </Button>
        </div>
      )}

      {/* TOKEN DISPLAY — visible once after generation */}
      {pairing?.pairing_token && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-md p-4 space-y-3" data-testid="tally-token-display">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-amber-700 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-amber-900">
              <strong>Copy this token NOW.</strong> It's shown only once. Generate a new one if you lose it.
            </div>
          </div>
          <div className="flex items-center gap-2 bg-white border border-amber-300 rounded p-2 font-mono text-sm break-all" data-testid="tally-token-value">
            {pairing.pairing_token}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={copyToken} variant="outline" size="sm" data-testid="tally-copy-token-btn">
              <Copy className="h-3.5 w-3.5 mr-1.5" /> {copied ? 'Copied!' : 'Copy token'}
            </Button>
          </div>
          <div className="text-xs text-amber-900/80">{pairing.instructions}</div>
        </div>
      )}

      {/* STEP 2 — download link + instructions */}
      {(isAwaiting || isActive || pairing) && (
        <div className="bg-white border border-border rounded-md p-4 space-y-3" data-testid="tally-connector-download">
          <div className="flex items-start gap-3">
            <Download className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-text-primary mb-0.5">Step 2 · Send the connector to the accountant</h3>
              <p className="text-xs text-text-secondary mb-2">
                Send these three things via WhatsApp / email to the accountant:
              </p>
              <ol className="text-xs space-y-2 list-decimal list-inside text-text-secondary">
                <li>
                  <strong>Download link:</strong>{' '}
                  <a href="/downloads/EpicureaTallyConnector.exe" download
                     className="text-primary hover:underline font-mono text-[11px]"
                     data-testid="tally-download-exe-link">
                    EpicureaTallyConnector.exe (Windows)
                  </a>
                </li>
                <li>
                  <strong>Or Python script:</strong>{' '}
                  <a href="/downloads/tally_connector.py" download
                     className="text-primary hover:underline font-mono text-[11px]"
                     data-testid="tally-download-py-link">
                    tally_connector.py (any OS)
                  </a>
                </li>
                <li>
                  <strong>Setup guide:</strong>{' '}
                  <a href="/downloads/tally_connector_README.md" download
                     className="text-primary hover:underline font-mono text-[11px]"
                     data-testid="tally-download-readme-link">
                    Installation README
                  </a>
                </li>
              </ol>
              <div className="text-xs text-text-muted mt-3 bg-zinc-50 border border-border rounded p-2">
                <strong>Tell the accountant:</strong> "Download the .exe, double-click it, paste the
                token I sent, click Save. Then set a Windows Task Scheduler entry to run
                <span className="font-mono"> EpicureaTallyConnector.exe --no-prompt</span> daily at 3 AM."
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      {(isActive || isManualOnly || isAwaiting) && isAdmin && (
        <div className="flex flex-wrap gap-2 pt-1">
          {isActive && (
            <Button onClick={generatePairing} disabled={busy.pair} variant="outline" size="sm"
                    data-testid="tally-rotate-token-btn">
              <KeyRound className="h-3.5 w-3.5 mr-1.5" /> Rotate pairing token
            </Button>
          )}
          <Button onClick={disconnect} disabled={busy.disc} variant="outline" size="sm"
                  className="text-state-error border-red-200 hover:bg-red-50"
                  data-testid="tally-disconnect-btn">
            {busy.disc ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 mr-1.5" />}
            Disconnect
          </Button>
        </div>
      )}

      {/* MANUAL XML UPLOAD — fallback */}
      <div className="bg-zinc-50 border border-border rounded-md p-4" data-testid="tally-xml-upload">
        <div className="flex items-start gap-3 mb-3">
          <Upload className="h-5 w-5 text-text-secondary mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-text-primary mb-0.5">Fallback · Manual XML upload</h3>
            <p className="text-xs text-text-secondary">
              For accountants who can't (or won't) install the connector. Export Day Book XML from Tally
              (<span className="font-mono">Display → Day Book → Alt+E → XML</span>) and drop it here.
            </p>
          </div>
        </div>
        <label className="flex items-center gap-3 bg-white border border-dashed border-border rounded p-3 cursor-pointer hover:border-primary transition-colors"
               data-testid="tally-xml-dropzone">
          <FileText className="h-5 w-5 text-text-muted" />
          <span className="text-xs text-text-secondary flex-1">
            {busy.upload ? 'Parsing XML…' : 'Click or drop a Tally Day-Book XML file'}
          </span>
          <input
            type="file"
            accept=".xml,application/xml,text/xml"
            className="hidden"
            disabled={busy.upload}
            onChange={(e) => uploadXml(e.target.files?.[0])}
            data-testid="tally-xml-file-input"
          />
        </label>
        {uploadResult && (
          <div className={`mt-3 text-xs rounded p-2 border ${uploadResult.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-red-50 border-red-200 text-red-900'}`}
               data-testid="tally-xml-upload-result">
            {uploadResult.ok
              ? <><CheckCircle2 className="inline h-3.5 w-3.5 mr-1" /> Parsed {uploadResult.parsed} vouchers — {uploadResult.inserted} new, {uploadResult.updated} updated. Total now: {uploadResult.total_vouchers_now}.</>
              : <><AlertCircle className="inline h-3.5 w-3.5 mr-1" /> {uploadResult.error}</>
            }
          </div>
        )}
      </div>

      {/* Sync history */}
      {logs.length > 0 && (
        <div data-testid="tally-sync-logs">
          <button onClick={() => setLogsOpen(o => !o)}
                  className="flex items-center gap-1 text-xs font-semibold text-text-secondary hover:text-primary">
            {logsOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            Recent sync history ({logs.length})
          </button>
          {logsOpen && (
            <div className="mt-2 border border-border rounded-md divide-y divide-border bg-white text-xs">
              {logs.slice(0, 8).map((l, i) => (
                <div key={l.id || i} className="px-3 py-1.5 flex items-center justify-between" data-testid={`tally-log-row-${i}`}>
                  <div className="flex items-center gap-2">
                    {l.errors > 0 ? <AlertCircle className="h-3 w-3 text-state-warning" /> : <CheckCircle2 className="h-3 w-3 text-state-success" />}
                    <span className="text-text-secondary">{new Date(l.ts).toLocaleString()}</span>
                    <span className="text-text-muted">· {l.source}</span>
                  </div>
                  <span className="text-text-muted font-mono">
                    +{l.inserted} ~{l.updated}{l.errors ? ` !${l.errors}` : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="text-xs text-state-error bg-red-50 border border-red-200 rounded p-2" data-testid="tally-panel-error">
          {error}
        </div>
      )}
    </div>
  );
};

const StatusPill = ({ status, isActive, isAwaiting, isManualOnly }) => {
  let cls, Icon, label;
  if (isActive) {
    cls = "text-state-success bg-emerald-50 border-emerald-200"; Icon = CheckCircle2; label = "Connected";
  } else if (isAwaiting) {
    cls = "text-amber-700 bg-amber-50 border-amber-200"; Icon = Clock; label = "Awaiting connector";
  } else if (isManualOnly) {
    cls = "text-blue-700 bg-blue-50 border-blue-200"; Icon = ShieldCheck; label = "Manual XML only";
  } else {
    cls = "text-text-muted bg-zinc-100 border-border"; Icon = AlertCircle; label = "Not configured";
  }
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold border rounded-full px-2.5 py-1 ${cls}`}
          data-testid={`tally-status-${(label || '').toLowerCase().replace(/[^a-z]/g, '-')}`}>
      <Icon className="h-3.5 w-3.5" /> {label}
    </span>
  );
};

const InfoTile = ({ label, value, mono, badge }) => (
  <div className="bg-white border border-border rounded-md p-2.5">
    <div className="text-[10px] uppercase tracking-wider text-text-muted font-semibold">{label}</div>
    <div className={`text-sm text-text-primary mt-0.5 ${mono ? 'font-mono' : ''}`}>{value}</div>
    {badge && (
      <div className={`text-[10px] mt-1 font-semibold ${
        badge === 'success' ? 'text-state-success' : 'text-state-warning'
      }`}>
        {badge}
      </div>
    )}
  </div>
);

export default TallyConnectPanel;
